import { Menu, MenuItem } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import CssBaseline from '@material-ui/core/CssBaseline';
import IconButton from '@material-ui/core/IconButton';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { MoreVert } from '@material-ui/icons';
import MenuIcon from '@material-ui/icons/Menu';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { UserService } from 'src/services/user.service';
import { clientRoutes, drawerWidth } from 'src/shared/constants';
import { useApp } from '../../providers/app.provider';
import SidebarComponent, { useStyles as useDrawerStyles } from '../Sidebar/sidebar.component';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			display: 'flex',
		},
		appBar: {
			transition: theme.transitions.create(['margin', 'width'], {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.leavingScreen,
			}),
		},
		appBarShift: {
			width: `calc(100% - ${drawerWidth}px)`,
			marginLeft: drawerWidth,
			transition: theme.transitions.create(['margin', 'width'], {
				easing: theme.transitions.easing.easeOut,
				duration: theme.transitions.duration.enteringScreen,
			}),
		},
		menuButton: {
			marginRight: theme.spacing(2),
		},
		hide: {
			display: 'none',
		},
		content: {
			flexGrow: 1,
			padding: theme.spacing(3),
			paddingTop: theme.spacing(2),
			transition: theme.transitions.create('margin', {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.leavingScreen,
			}),
			marginLeft: -drawerWidth,
			height: '100vh',
		},
		contentShift: {
			transition: theme.transitions.create('margin', {
				easing: theme.transitions.easing.easeOut,
				duration: theme.transitions.duration.enteringScreen,
			}),
			marginLeft: 0,
			backgroundPosition: `bottom left+${drawerWidth}px`,
		},
		toolbarHeight: {
			minHeight: "56px"
		}
	})
);

const LayoutComponent: React.FC = (props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const draweClasses = useDrawerStyles(); 
	const history = useHistory();
	const { isSidebarOpen, toggleSidebar } = useApp();
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

	const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	  };
	
	  const handleMenuClose = () => {
		setAnchorEl(null);
	  };

	const renderMenu = (
		<React.Fragment>
			<IconButton onClick={handleMenuClick}>
				<MoreVert style={{fill: 'white'}} />
			</IconButton>
			<Menu
				id="simple-menu"
				anchorEl={anchorEl}
				keepMounted
				open={Boolean(anchorEl)}
				onClose={handleMenuClose}
				>
					<MenuItem
						onClick={() => {
							UserService.SignOut();
							handleMenuClose();
						}}
					>
						{t("label-sign-out")}
					</MenuItem>
			</Menu>
		</React.Fragment>
	);


	const getTitle = () => {
		switch (history.location.pathname) {
			case clientRoutes.dashboard:
				return t('label-dashboard')
			case clientRoutes.efileAnalyzer:
				return t('label-efile-analyzer')
			case clientRoutes.realSizeViewer:
				return t('label-realsize-viewer')
			case clientRoutes.geocoder:
				return t('label-geocoder')
			case clientRoutes.volumetricWeight:
				return t('label-volumetric-weight')
			case clientRoutes.unitConverter:
				return t('label-unit-converter')
			default:
				return ''
		}
	}

	if(history.location.pathname === '/signin' || history.location.pathname === '/signup') {
		return <>{props.children}</>
	}

	return (
		<div className={classes.root}>
			<CssBaseline />
			<AppBar
				position='fixed'
				className={clsx(classes.appBar, {
					[classes.appBarShift]: isSidebarOpen,
				})}
			>
				<Toolbar className={classes.toolbarHeight}>
					<IconButton
						color='inherit'
						aria-label='open drawer'
						onClick={() => toggleSidebar()}
						edge='start'
						className={clsx(classes.menuButton, isSidebarOpen && classes.hide)}
					>
						<MenuIcon />
					</IconButton>
					<Typography variant='h6' noWrap>
						{getTitle()}
					</Typography>
					<div className='flex-spacer'>{renderMenu}</div>
				</Toolbar>
			</AppBar>
			<SidebarComponent />
			<main
				className={clsx(classes.content, {
					[classes.contentShift]: isSidebarOpen,
				})}
			>
				<div className={draweClasses.drawerHeader} />
				{props.children}
			</main>
		</div>
	);
};

export default LayoutComponent;
